import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { API_CALL_FAILED } from '@/consts/events';
import { env } from '@/env';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useRegistration } from '@/context/RegistrationContext';
import { useWregDev } from '@/components/webreg/dev/WregDevContext';
import { apiClient } from '@/services/api';
import { WREG_ENTRY_PATH, wregCarriedSearch } from '@/components/webreg/wregRoutes';
import { signupErrStatus } from '@/components/webreg/useWregSignup';
import { WREG_STEP_NAME } from '@/components/webreg/wregSteps';

export const wregEntryPromoCode = (search: string): string =>
  new URLSearchParams(search).get('promo')?.trim() || env.ACCESS_PILOT_PROMO_CODE;

export const useWregPromoEntry = (): boolean => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { isLoaded, registrationData, setPromoCodeData } = useRegistration();
  const { trackError } = useAnalytics();
  const dev = useWregDev();
  const [settled, setSettled] = useState(false);
  const startedRef = useRef(false);

  const code = wregEntryPromoCode(searchParams.toString());
  const needed = dev.live && isLoaded && registrationData.promoCodeData === null;

  const searchRef = useRef(location.search);
  searchRef.current = location.search;

  const bounce = useCallback(() => {
    navigate(
      { pathname: WREG_ENTRY_PATH, search: wregCarriedSearch(searchRef.current) },
      { replace: true },
    );
  }, [navigate]);

  useEffect(() => {
    if (!needed || startedRef.current) return;
    startedRef.current = true;

    let alive = true;

    const run = async () => {
      try {
        const response = await apiClient.promoCode(code);
        if (!alive) return;
        if (response.capReached === true) {
          bounce();
          return;
        }
        setPromoCodeData(response);
        setSettled(true);
      } catch (error) {
        if (!alive) return;
        trackError({
          event: API_CALL_FAILED,
          properties: {
            service: 'promo',
            status: signupErrStatus(error),
            step_name: WREG_STEP_NAME.intake,
          },
        });
        bounce();
      }
    };

    void run();

    return () => {
      alive = false;
    };
  }, [bounce, code, needed, setPromoCodeData, trackError]);

  return !needed || settled;
};
